// ============================================================
// ProductionTab.jsx  —  Noble Enterprises (tape module)
//
// STEP 1 OF PRODUCTION: jumbo roll → cores
//
// WHAT'S ON THIS SCREEN
//   • Record a core batch against a jumbo roll
//   • Batch history, click to expand
//   • Yield variance report (button, opens a modal)
//
// THE ARITHMETIC
//   meters consumed = cores × core length
// Only the meters that became cores come off the roll. Trim and
// waste are never subtracted, so the roll's book balance stays
// above reality until it is closed — and that gap is the loss
// the variance report shows.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. BatchForm       — record a batch
//   C. VarianceModal   — the yield report
//   D. ProductionTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Check, BarChart3, ChevronDown as Chevron } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money  = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const meters = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })} m`;
const today  = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && (
      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>
    )}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const Modal = ({ title, subtitle, onClose, width = '900px', children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#ffffff', borderRadius: '14px', width, maxWidth: '100%',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}
    >
      <div style={{
        padding: '20px 24px', borderBottom: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'flex-start', gap: '14px',
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: C.text }}>{title}</h3>
          {subtitle && <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>{subtitle}</p>}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: '2px' }}
        >
          <X size={20} />
        </button>
      </div>
      <div style={{ padding: '22px 24px', overflowY: 'auto' }}>{children}</div>
    </div>
  </div>
);

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCard = ({ label, value, colour = C.text }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '16px 18px',
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
      {value}
    </div>
  </div>
);


// ============================================================
// B. BATCH FORM
//
// The live calculation is the point of this form. Picking a
// roll and typing a core count immediately shows what comes
// off the roll and what's left — so a typo is obvious before
// it's saved.
// ============================================================

const BatchForm = ({ form, setForm, rolls, onSave, onCancel, saving }) => {
  const roll = rolls.find(r => String(r.id) === String(form.jumbo_roll_id));

  const coreLength = roll ? Number(roll.core_length_meters) : 0;
  const remaining  = roll ? Number(roll.remaining_meters) : 0;
  const cores      = Number(form.cores_produced || 0);

  const metersConsumed = cores * coreLength;
  const metersAfter    = remaining - metersConsumed;
  const maxCores       = coreLength > 0 ? Math.floor(remaining / coreLength) : 0;
  const tooMany        = cores > maxCores;

  return (
    <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        <Field label="Jumbo roll">
          <select
            style={inputStyle}
            value={form.jumbo_roll_id}
            onChange={e => setForm({ ...form, jumbo_roll_id: e.target.value })}
          >
            <option value="">Select roll…</option>
            {rolls.map(r => (
              <option key={r.id} value={r.id}>
                {r.vendor_roll_number} — {meters(r.remaining_meters)} left
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Cores produced"
          hint={roll ? `Up to ${maxCores} at ${coreLength} m each` : undefined}
        >
          <TextInput
            type="number" min="1"
            value={form.cores_produced}
            onChange={e => setForm({ ...form, cores_produced: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Scrap collected (kg)">
          <TextInput
            type="number" step="0.01" min="0"
            value={form.scrap_kg}
            onChange={e => setForm({ ...form, scrap_kg: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Production date">
          <TextInput
            type="date"
            max={today()}
            value={form.production_date}
            onChange={e => setForm({ ...form, production_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      {/* Live arithmetic */}
      {roll && cores > 0 && (
        <div style={{
          background: '#ffffff',
          border: `1px solid ${tooMany ? C.danger : C.border}`,
          borderRadius: '8px', padding: '14px 18px', marginBottom: '18px',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '14px',
          }}>
            {[
              ['Roll has', meters(remaining), C.textSoft],
              ['This batch takes', meters(metersConsumed), C.text],
              ['Leaves', meters(Math.max(0, metersAfter)), tooMany ? C.danger : C.text],
            ].map(([label, value, colour]) => (
              <div key={label}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '17px', fontWeight: '700', color: colour, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '10px' }}>
            {cores} core{cores === 1 ? '' : 's'} × {coreLength} m
          </div>

          {tooMany && (
            <div style={{
              marginTop: '12px', padding: '10px 13px', borderRadius: '7px',
              background: C.dangerBg, color: C.danger, fontSize: '13px',
            }}>
              This roll only has enough for {maxCores} core{maxCores === 1 ? '' : 's'}.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={saving || tooMany}>
          <Check size={16} /> {saving ? 'Saving…' : 'Record batch'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};


// ============================================================
// C. YIELD VARIANCE MODAL
//
// The report the owner asked for: what each roll should have
// yielded against what it actually gave.
//
// Only closed rolls carry a verdict. An open roll is still
// being worked through, so judging its yield would be wrong.
// ============================================================

// Under 6% loss is normal trim. 6-12% is worth a word. Above
// that is a problem. Without bands every roll looks bad and
// the owner stops reading the report.
const varianceBand = (pct) => {
  const loss = Math.abs(pct);
  if (loss < 6)  return { colour: C.success, bg: C.successBg };
  if (loss < 12) return { colour: C.warning, bg: C.warningBg };
  return { colour: C.danger, bg: C.dangerBg };
};

const VarianceModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/tape/core-batches/variance?limit=30`, { headers })
      .then(res => setData(res.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const cell = { padding: '10px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: C.rowAlt, fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  const headline = data?.headline;
  const pct = headline?.variance_pct;

  return (
    <Modal
      title="Yield variance"
      subtitle="Cores each roll should have given, against what it actually gave"
      onClose={onClose}
    >
      {loading && <p style={{ color: C.textSoft }}>Loading…</p>}

      {!loading && data && (
        <>
          {/* Headline */}
          <div style={{
            background: C.rowAlt, borderRadius: '10px', padding: '18px 20px',
            marginBottom: '20px',
          }}>
            {headline.reliable && pct != null ? (
              <>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  Last {headline.roll_count} closed rolls
                </div>
                <div style={{
                  fontSize: '32px', fontWeight: '700', marginTop: '4px',
                  color: varianceBand(pct).colour,
                }}>
                  {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                </div>
                <div style={{ fontSize: '13px', color: C.textSoft, marginTop: '4px' }}>
                  {headline.actual} cores from an expected {headline.expected}
                </div>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: C.textSoft }}>
                Not enough closed rolls yet for a reliable figure.
                {' '}{headline.roll_count} closed so far — five are needed before
                an average means anything.
              </div>
            )}
          </div>

          <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: 'left' }}>Roll</th>
                    <th style={{ ...head, textAlign: 'left' }}>Vendor</th>
                    <th style={{ ...head, textAlign: 'right' }}>Expected</th>
                    <th style={{ ...head, textAlign: 'right' }}>Actual</th>
                    <th style={{ ...head, textAlign: 'right' }}>Variance</th>
                    <th style={{ ...head, textAlign: 'right' }}>Unaccounted</th>
                    <th style={{ ...head, textAlign: 'right' }}>Loss value</th>
                    <th style={{ ...head, textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(row => {
                    const expected = Number(row.expected_cores);
                    const actual   = Number(row.actual_cores);
                    const isClosed = row.status === 'closed';
                    const rowPct   = expected > 0 ? ((actual - expected) / expected) * 100 : 0;
                    const band     = varianceBand(rowPct);

                    return (
                      <tr key={row.id}>
                        <td style={{ ...cell, fontWeight: '600' }}>
                          {row.vendor_roll_number}
                          <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '400' }}>
                            {meters(row.total_meters)}
                          </div>
                        </td>
                        <td style={cell}>{row.vendor_name}</td>
                        <td style={{ ...cell, textAlign: 'right', color: C.textSoft }}>{expected}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{actual}</td>

                        {/* Only a closed roll gets a verdict */}
                        <td style={{ ...cell, textAlign: 'right' }}>
                          {isClosed ? (
                            <span style={{
                              fontWeight: '700', fontSize: '12px',
                              padding: '3px 9px', borderRadius: '10px',
                              background: band.bg, color: band.colour,
                            }}>
                              {rowPct > 0 ? '+' : ''}{rowPct.toFixed(0)}%
                            </span>
                          ) : (
                            <span style={{ color: C.textMuted }}>—</span>
                          )}
                        </td>

                        <td style={{ ...cell, textAlign: 'right', color: C.textSoft }}>
                          {isClosed ? meters(row.unaccounted_meters) : '—'}
                        </td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>
                          {isClosed ? money(row.loss_value) : '—'}
                        </td>
                        <td style={{ ...cell, color: C.textSoft, textTransform: 'capitalize' }}>
                          {isClosed ? `Closed ${fmtDate(row.closed_at)}` : 'Active'}
                        </td>
                      </tr>
                    );
                  })}

                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan="8" style={{ ...cell, textAlign: 'center', color: C.textMuted, padding: '32px' }}>
                        No rolls yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p style={{ fontSize: '12px', color: C.textMuted, margin: '12px 0 0', lineHeight: 1.6 }}>
            Under 6% loss is normal trim. 6–12% is worth checking. Above 12% is flagged.
            Only closed rolls carry a verdict — an open roll is still being worked through.
          </p>
        </>
      )}

      {!loading && !data && (
        <p style={{ color: C.danger }}>Could not load the report.</p>
      )}
    </Modal>
  );
};


// ============================================================
// D. MAIN COMPONENT
// ============================================================

const emptyForm = {
  jumbo_roll_id: '',
  cores_produced: '',
  scrap_kg: '',
  production_date: today(),
  notes: '',
};

const ProductionTab = () => {
  const [batches, setBatches] = useState([]);
  const [rolls, setRolls]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showVariance, setShowVariance] = useState(false);
  const [banner, setBanner]   = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [b, r, s] = await Promise.all([
        axios.get(`${API}/tape/core-batches`, { headers }),
        axios.get(`${API}/tape/core-batches/rolls/available`, { headers }),
        axios.get(`${API}/tape/core-batches/summary`, { headers }),
      ]);
      setBatches(b.data.data || []);
      setRolls(r.data.data || []);
      setSummary(s.data.data || null);
    } catch {
      notify('error', 'Could not load production. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const f = form;
    if (!f.jumbo_roll_id)    return notify('error', 'Pick a jumbo roll');
    if (!f.production_date)  return notify('error', 'Production date is required');

    const cores = Number(f.cores_produced);
    if (!Number.isInteger(cores) || cores <= 0) {
      return notify('error', 'Cores produced must be a whole number above 0');
    }

    setSaving(true);
    try {
      await axios.post(`${API}/tape/core-batches`, {
        jumbo_roll_id: f.jumbo_roll_id,
        cores_produced: cores,
        scrap_kg: f.scrap_kg,
        production_date: f.production_date,
        notes: f.notes,
      }, { headers });

      notify('success', 'Core batch recorded');
      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete batch ${row.batch_number}? The meters go back on the roll and the cores leave the pool.`
    )) return;

    try {
      await axios.delete(`${API}/tape/core-batches/${row.id}`, { headers });
      notify('success', 'Batch deleted');
      if (expandedId === row.id) setExpandedId(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        <StatCard label="Cores in pool"    value={summary?.cores_in_pool ?? '—'} colour={C.brand} />
        <StatCard label="Made this month"  value={summary?.cores_this_month ?? '—'} />
        <StatCard label="Total batches"    value={summary?.batch_count ?? '—'} />
        <StatCard label="Scrap collected"  value={summary ? `${Number(summary.scrap_kg_total).toFixed(1)} kg` : '—'} colour={C.textSoft} />
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Core production
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Jumbo rolls rewound into cores. Cores go to the open pool.
            </p>
          </div>

          <Btn variant="ghost" onClick={() => setShowVariance(true)}>
            <BarChart3 size={16} /> Yield variance
          </Btn>

          {!form && (
            <Btn onClick={() => setForm(emptyForm)} disabled={rolls.length === 0}>
              <Plus size={16} /> Record batch
            </Btn>
          )}
        </div>

        {!form && rolls.length === 0 && !loading && (
          <div style={{ padding: '14px 22px', background: C.infoBg, fontSize: '13px', color: C.info }}>
            No jumbo rolls with enough meters left. Receive stock first.
          </div>
        )}

        {form && (
          <BatchForm
            form={form}
            setForm={setForm}
            rolls={rolls}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Batch</th>
                <th style={thStyle}>Roll</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cores made</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>In pool</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Meters used</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Scrap</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && batches.map(row => {
                const isOpen   = expandedId === row.id;
                const inPool   = Number(row.cores_remaining);
                const made     = Number(row.cores_produced);
                const allUsed  = inPool === 0;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : row.id)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.batch_number}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {row.vendor_roll_number}
                        <div style={{ fontSize: '11px', color: C.textMuted }}>{row.tape_type_name}</div>
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.production_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{made}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{
                          fontWeight: '600',
                          color: allUsed ? C.textMuted : C.brand,
                        }}>
                          {inPool}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {meters(row.meters_consumed)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {Number(row.scrap_kg).toFixed(1)} kg
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {/* Only while every core is still unused —
                            once cut, the finished rolls need this batch */}
                        {inPool === made && (
                          <IconBtn
                            danger
                            title="Delete batch"
                            onClick={(e) => { e.stopPropagation(); remove(row); }}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        )}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan="8" style={{
                          padding: '18px 22px', background: C.rowAlt,
                          borderTop: `1px solid ${C.borderSoft}`,
                        }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '12px',
                          }}>
                            {[
                              ['Jumbo', row.jumbo_name],
                              ['Vendor', row.vendor_name],
                              ['Core length', meters(row.core_length_meters)],
                              ['Core width', `${row.core_width_inches}"`],
                              ['Cores used', row.cores_used],
                              ['Roll now has', meters(row.roll_remaining_meters)],
                            ].map(([label, value]) => (
                              <div key={label} style={{
                                background: '#ffffff', border: `1px solid ${C.border}`,
                                borderRadius: '8px', padding: '12px 14px',
                              }}>
                                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                                  {label}
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: '700', color: C.text, marginTop: '3px' }}>
                                  {value}
                                </div>
                              </div>
                            ))}
                          </div>

                          {row.notes && (
                            <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
                              <strong style={{ color: C.text }}>Notes:</strong> {row.notes}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {!loading && batches.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No core batches yet. Click "Record batch" to start production.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showVariance && <VarianceModal onClose={() => setShowVariance(false)} />}
    </div>
  );
};

export default ProductionTab;